import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOrderDetails,
  updateOrderStatus,
  selectOrderDetails,
  clearOrderDetails,
} from "../../../slice/order/orderSlice";
import api from "../../../api/axios";
const BASE_URL = process.env.REACT_APP_API_URL;

/* ─── Constants ─────────────────────────────────────────────── */
const STATUS_CONFIG = {
  pending:      { bg: "#faeeda", color: "#854f0b", border: "#fac775", dot: "#f59e0b", label: "Pending"           },
  accepted:     { bg: "#e6f1fb", color: "#185fa5", border: "#b5d4f4", dot: "#3b82f6", label: "Accepted"          },
  preparing:    { bg: "#eeedfe", color: "#534ab7", border: "#afa9ec", dot: "#6362e7", label: "Started Preparing"  },
  ready:        { bg: "#eaf3de", color: "#3b6d11", border: "#c0dd97", dot: "#22c55e", label: "Order Ready"        },
  out_delivery: { bg: "#fbeaf0", color: "#993556", border: "#f4c0d1", dot: "#ec4899", label: "Out for Delivery"   },
  out_pickup:   { bg: "#fbeaf0", color: "#993556", border: "#f4c0d1", dot: "#ec4899", label: "Out for Pickup"     },
  delivered:    { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Delivered"          },
  shipped:      { bg: "#e1f5ee", color: "#0f6e56", border: "#9fe1cb", dot: "#10b981", label: "Shipped"            },
  cancelled:    { bg: "#f3f4f6", color: "#6b7280", border: "#d1d5db", dot: "#9ca3af", label: "Cancelled"          },
};

/* Workflow steps per delivery mode */
const TIMELINE_STEPS = {
  delivery: [
    { key: "pending",      label: "Pending",           icon: "🕐" },
    { key: "accepted",     label: "Accepted",           icon: "✅" },
    { key: "preparing",    label: "Started Preparing",  icon: "👨‍🍳" },
    { key: "ready",        label: "Order Ready",        icon: "📦" },
    { key: "out_delivery", label: "Out for Delivery",   icon: "🚚" },
    { key: "delivered",    label: "Delivered",          icon: "🎉" },
  ],
  pickup: [
    { key: "pending",     label: "Pending",           icon: "🕐" },
    { key: "accepted",    label: "Accepted",           icon: "✅" },
    { key: "preparing",   label: "Started Preparing",  icon: "👨‍🍳" },
    { key: "ready",       label: "Order Ready",        icon: "📦" },
    { key: "out_pickup",  label: "Out for Pickup",     icon: "🏪" },
    { key: "delivered",   label: "Delivered",          icon: "🎉" },
  ],
};

/* Status → step index map */
const STATUS_STEP_INDEX = {
  pending:      0,
  accepted:     1,
  preparing:    2,
  ready:        3,
  out_delivery: 4,
  out_pickup:   4,
  delivered:    5,
  shipped:      5,
  cancelled:    -1,
};

const AVATAR_PALETTE = [
  ["#eff6ff", "#6362e7"], ["#faf5ff", "#7c3aed"],
  ["#f0fdf4", "#16a34a"], ["#fff7ed", "#ea580c"], ["#f0f9ff", "#0284c7"],
];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

/* ─── Small reusable card section header ─────────────────────── */
const CardHeader = ({ iconBg, iconStroke, icon, title }) => (
  <div className="card-header" style={{ borderBottom: "1px solid #f3f4f6", paddingBottom: 14 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 34, height: 34, borderRadius: 9, background: iconBg,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
          fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {icon}
        </svg>
      </div>
      <h6 style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#111827" }}>{title}</h6>
    </div>
  </div>
);

/* ─── InfoRow ─────────────────────────────────────────────────── */
const InfoRow = ({ emoji, value, last }) => (
  <div style={{
    display: "flex", gap: 10, marginBottom: last ? 0 : 10,
    paddingBottom: last ? 0 : 10,
    borderBottom: last ? "none" : "1px solid #f3f4f6",
    alignItems: "flex-start",
  }}>
    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{emoji}</span>
    <span style={{ fontSize: 12, color: "#374151", lineHeight: 1.6 }}>{value || "—"}</span>
  </div>
);

/* ─── Order Timeline Component ───────────────────────────────── */
const OrderTimeline = ({ order }) => {
  const deliveryMode  = order?.deleveryStatus || "delivery";
  const currentStatus = order?.status || "pending";
  const isCancelled   = currentStatus === "cancelled";

  const steps        = TIMELINE_STEPS[deliveryMode] || TIMELINE_STEPS.delivery;
  const currentStep  = STATUS_STEP_INDEX[currentStatus] ?? 0;

  return (
    <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
      <CardHeader
        iconBg="#f0fdf4" iconStroke="#0f6e56"
        title="Order Timeline"
        icon={<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>}
      />

      <div className="card-body" style={{ padding: "24px 20px" }}>

        {/* ── Cancelled banner ── */}
        {isCancelled && (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#fef2f2", border: "1.5px solid #fca5a5",
            borderRadius: 12, padding: "12px 16px", marginBottom: 24,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#dc2626" }}>Order Cancelled</div>
              <div style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>This order has been cancelled</div>
            </div>
          </div>
        )}

        {/* ── Horizontal step bar (non-cancelled) ── */}
        {!isCancelled && (
          <>
            {/* Progress bar track */}
            <div className="sp_horizontal" style={{ position: "relative", marginBottom: 8 }}>
              <div style={{
                position: "absolute", top: 18, left: `calc(100% / ${steps.length} / 2)`,
                right: `calc(100% / ${steps.length} / 2)`,
                height: 4, background: "#f3f4f6", borderRadius: 4, zIndex: 0,
              }}>
                <div style={{
                  height: "100%", borderRadius: 4,
                  background: "linear-gradient(90deg, #0f6e56, #22c55e)",
                  width: currentStep === 0
                    ? "0%"
                    : `${(currentStep / (steps.length - 1)) * 100}%`,
                  transition: "width 0.5s ease",
                }} />
              </div>

              {/* Steps */}
              <div style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
                {steps.map((step, i) => {
                  const isDone   = i < currentStep;
                  const isActive = i === currentStep;
                  const isPast   = i > currentStep;

                  return (
                    <div key={step.key} style={{
                      display: "flex", flexDirection: "column", alignItems: "center",
                      flex: 1, gap: 8,
                    }}>
                      {/* Circle */}
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: isDone ? "#0f6e56" : isActive ? "#fff" : "#f9fafb",
                        border: isDone
                          ? "2.5px solid #0f6e56"
                          : isActive
                            ? "2.5px solid #0f6e56"
                            : "2.5px solid #e5e7eb",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isActive ? "0 0 0 5px #e1f5ee" : isDone ? "0 0 0 3px #e1f5ee" : "none",
                        transition: "all 0.3s",
                        position: "relative",
                      }}>
                        {isDone ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        ) : isActive ? (
                          <div style={{
                            width: 10, height: 10, borderRadius: "50%", background: "#0f6e56",
                            animation: "pulse 1.5s infinite",
                          }} />
                        ) : (
                          <span style={{ fontSize: 14, opacity: isPast ? 0.3 : 0.5 }}>{step.icon}</span>
                        )}
                      </div>

                      {/* Label */}
                      <div style={{
                        fontSize: 10, fontWeight: isActive ? 700 : isDone ? 600 : 400,
                        color: isDone ? "#0f6e56" : isActive ? "#0f6e56" : "#9ca3af",
                        textAlign: "center", lineHeight: 1.3,
                        maxWidth: 70,
                      }}>
                        {step.label}
                      </div>

                      {/* Active badge */}
                      {isActive && (
                        <span style={{
                          background: "#e1f5ee", color: "#0f6e56",
                          border: "1px solid #9fe1cb",
                          fontSize: 9, fontWeight: 700,
                          padding: "2px 7px", borderRadius: 20,
                          whiteSpace: "nowrap",
                        }}>
                          Current
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Vertical detail list below ── */}
            <div className="sp_vertical" style={{
              marginTop: 28, borderTop: "1px solid #f3f4f6", paddingTop: 20,
              display: "flex", flexDirection: "column", gap: 0,
            }}>
              {steps.map((step, i) => {
                const isDone   = i < currentStep;
                const isActive = i === currentStep;
                const isLast   = i === steps.length - 1;

                return (
                  <div key={step.key} style={{ display: "flex", gap: 14, position: "relative" }}>
                    {/* Left line + dot */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 20 }}>
                      <div style={{
                        width: 14, height: 14, borderRadius: "50%", flexShrink: 0, marginTop: 2,
                        background: isDone ? "#0f6e56" : isActive ? "#fff" : "#f3f4f6",
                        border: `2px solid ${isDone ? "#0f6e56" : isActive ? "#0f6e56" : "#d1d5db"}`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        boxShadow: isActive ? "0 0 0 3px #e1f5ee" : "none",
                        zIndex: 1,
                      }}>
                        {isDone && (
                          <svg width="6" height="6" viewBox="0 0 24 24" fill="none"
                            stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                        {isActive && !isDone && (
                          <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#0f6e56" }} />
                        )}
                      </div>
                      {!isLast && (
                        <div style={{
                          width: 2, flex: 1, minHeight: 28,
                          background: isDone ? "#0f6e56" : "#e5e7eb",
                          borderRadius: 2, marginTop: 2,
                        }} />
                      )}
                    </div>

                    {/* Content */}
                    <div style={{ flex: 1, paddingBottom: isLast ? 0 : 20 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{
                          fontWeight: isDone || isActive ? 600 : 400,
                          fontSize: 13,
                          color: isDone ? "#111827" : isActive ? "#0f6e56" : "#9ca3af",
                        }}>
                          {step.icon} {step.label}
                        </span>
                        {isActive && (
                          <span style={{
                            background: "#e1f5ee", color: "#0f6e56",
                            border: "1px solid #9fe1cb",
                            fontSize: 10, fontWeight: 700,
                            padding: "2px 8px", borderRadius: 20,
                          }}>
                            Current
                          </span>
                        )}
                        {isDone && (
                          <span style={{
                            background: "#f0fdf4", color: "#16a34a",
                            fontSize: 10, fontWeight: 600,
                            padding: "2px 8px", borderRadius: 20,
                          }}>
                            ✓ Done
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%   { transform: scale(1);   opacity: 1; }
          50%  { transform: scale(1.4); opacity: 0.6; }
          100% { transform: scale(1);   opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────── */
const CustomerOrderDetails = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const order     = useSelector(selectOrderDetails);
  const loading   = useSelector((s) => s.orders.loading);
  const updating  = useSelector((s) => s.orders.updating);
  const error     = useSelector((s) => s.orders.error);

  const [newStatus,      setNewStatus]      = useState("");
  const [trackingUrl,    setTrackingUrl]    = useState("");
  const [trackingDirty,  setTrackingDirty]  = useState(false);
  const [savedMsg,       setSavedMsg]       = useState("");
  const [trackingError,  setTrackingError]  = useState("");

  useEffect(() => {
    dispatch(fetchOrderDetails(id));
    return () => dispatch(clearOrderDetails());
  }, [dispatch, id]);

  useEffect(() => {
    if (order) {
      setNewStatus(String(order.orderStatus || 1));
      setTrackingUrl(order.trackingUrl || "");
      setTrackingDirty(false);
    }
  }, [order]);

  const handleSave = async () => {
    setTrackingError("");
    const statusNum = Number(newStatus);

    if (statusNum !== order.orderStatus) {
      await dispatch(updateOrderStatus({ orderId: order._id, orderStatus: statusNum }));
    }

    if (trackingDirty) {
      try {
        await api.patch(`/api/orders/${order._id}/tracking`, { trackingUrl });
        setTrackingDirty(false);
      } catch (err) {
        setTrackingError(err.response?.data?.message || "Failed to update tracking URL");
        return;
      }
    }

    setSavedMsg("Changes saved!");
    setTimeout(() => setSavedMsg(""), 2500);
    dispatch(fetchOrderDetails(id));
  };

  if (loading || !order) {
    return (
      <div className="container-fluid" style={{ paddingTop: 60, textAlign: "center", color: "#9ca3af" }}>
        {loading ? (
          <>
            <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
            Loading order…
          </>
        ) : (
          <span style={{ color: "#ef4444" }}>{error || "Order not found."}</span>
        )}
      </div>
    );
  }

  const sc          = STATUS_CONFIG[order.status] || STATUS_CONFIG["pending"];
  const addr        = order.shippingAddress || {};
  const avatarIdx   = order._id ? order._id.charCodeAt(order._id.length - 1) % AVATAR_PALETTE.length : 0;
  const avatarStyle = AVATAR_PALETTE[avatarIdx];

  return (
    <div>
      <div className="container-fluid" style={{ marginBottom: "30px" }}>

        {/* ── Top Bar ── */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 22, flexWrap: "wrap", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: "15px" }}>
            <button
             onClick={() => navigate(-1)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "#f9fafb", border: "1px solid #e5e7eb",
                borderRadius: 8, padding: "7px 14px", cursor: "pointer",
                fontSize: 13, fontWeight: 600, color: "#374151",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#f3f4f6"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#f9fafb"}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
              </svg>
              Back
            </button>
            <div>
              <span style={{ fontWeight: 800, fontSize: 19, color: "#111827", letterSpacing: "-0.3px" }}>
                {order.orderId}
              </span>
              <span style={{ marginLeft: 10, fontSize: 12, color: "#9ca3af" }}>
                {formatDateTime(order.createdAt)}
              </span>
            </div>
          </div>

          {/* Status + payment badges */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
              borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />
              {sc.label}
            </span>
            <span style={{
              background: order.payment?.status === "paid" ? "#e1f5ee" : "#faeeda",
              color:      order.payment?.status === "paid" ? "#0f6e56" : "#854f0b",
              border:     `1.5px solid ${order.payment?.status === "paid" ? "#9fe1cb" : "#fac775"}`,
              borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
            }}>
             {
  order.payment?.method?.toUpperCase() === "RAZORPAY"
    ? "Online Payment"
    : order.payment?.method?.toUpperCase() || "—"
}
{" · "}
{order.payment?.status || "—"}
            </span>
            {order.trackingUrl && (
              <a
                href={order.trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: "#f0f9ff", color: "#0284c7", border: "1.5px solid #bae6fd",
                  borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700,
                  textDecoration: "none", display: "flex", alignItems: "center", gap: 5,
                }}
              >
                Track Shipment
              </a>
            )}
          </div>
        </div>

        {/* ── Main Grid ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: 20, alignItems: "start" }}>

          {/* ════ LEFT COLUMN ════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Order Items */}
            <div className="card" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#eff6ff" iconStroke="#6362e7"
                title="Order Items"
                icon={<><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></>}
              />
              <div className="card-body" style={{ padding: 0 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {["Product", "Name", "Qty", "Unit Price", "Total"].map((h, i) => (
                        <th key={h} style={{
                          padding: "10px 16px",
                          textAlign: i === 0 ? "left" : i === 4 ? "right" : "center",
                          fontSize: 11, fontWeight: 700, color: "#6b7280",
                          textTransform: "uppercase", letterSpacing: "0.05em",
                        }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {(order.items || []).map((item, i) => (
                      <tr key={i} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                              width: 42, height: 42, borderRadius: 10,
                              background: "#f3f4f6", overflow: "hidden",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              flexShrink: 0,
                            }}>
                              {item.images?.[0] ? (
                                <img src={`${BASE_URL}/uploads/${item.images[0]}`}  alt={item.name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                  fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                                  <polyline points="21 15 16 10 5 21"/>
                                </svg>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>
                            {item.name || item.product?.name || "—"}
                          </div>
                          {item.texts?.length > 0 && (
                            <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                              Text: {item.texts.join(", ")}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center", fontWeight: 700, fontSize: 14, color: "#111827" }}>
                          {item.quantity}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "center", fontSize: 13, color: "#6b7280" }}>
                          ${(item.price || 0).toLocaleString("en-IN")}
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: 700, fontSize: 14, color: "#111827" }}>
                          ${(item.totalPrice || (item.price * item.quantity) || 0).toLocaleString("en-IN")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Price summary */}
                <div style={{ borderTop: "2px solid #f3f4f6", padding: "16px 20px" }}>
                  <div style={{ maxWidth: 280, marginLeft: "auto" }}>
                    {[
                      { label: "Subtotal", value: order.subtotal || 0 },
                      { label: "Discount", value: -(order.discount || 0), minus: true },
                      { label: "Shipping", value: order.shipping || 0 },
                    ].map((row) => (
                      <div key={row.label} style={{
                        display: "flex", justifyContent: "space-between",
                        marginBottom: 7, fontSize: 13, color: "#6b7280",
                      }}>
                        <span>{row.label}</span>
                        <span style={{ color: row.minus ? "#ef4444" : undefined }}>
                          {row.minus ? "−" : ""}${Math.abs(row.value).toLocaleString("en-IN")}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      display: "flex", justifyContent: "space-between",
                      borderTop: "1.5px solid #e5e7eb", paddingTop: 11, marginTop: 6,
                      fontWeight: 800, fontSize: 15, color: "#111827",
                    }}>
                      <span>Order Total</span>
                      <span style={{ color: "#6362e7" }}>${(order.totalAmount || 0).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Order Timeline ── */}
            <OrderTimeline order={order} />

          </div>

          {/* ════ RIGHT COLUMN ════ */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* Customer Card */}
            <div className="card padding" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#fff7ed" iconStroke="#ea580c"
                title="Customer"
                icon={<><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>}
              />
              <div className="card-body mt-3">
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                  <div style={{
                    width: 50, height: 50, borderRadius: 13,
                    background: avatarStyle[0], color: avatarStyle[1],
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontWeight: 800, fontSize: 16, letterSpacing: 1,
                    border: `2px solid ${avatarStyle[1]}30`, flexShrink: 0,
                  }}>
                    {getInitials(addr.fullName)}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{addr.fullName || "—"}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>Customer</div>
                  </div>
                </div>
                <InfoRow emoji="✉️" value={addr.email} />
                <InfoRow emoji="📞" value={addr.phone} />
                <InfoRow emoji="📍" value={[addr.address, addr.district, addr.state, addr.pincode].filter(Boolean).join(", ")} last />
              </div>
            </div>

            {/* Payment Card */}
            <div className="card padding" style={{ marginBottom: 0 }}>
              <CardHeader
                iconBg="#f0fdf4" iconStroke="#16a34a"
                title="Payment"
                icon={<><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></>}
              />
              <div className="card-body mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  {
  label: "Method",
  value:
    order.payment?.method?.toUpperCase() === "RAZORPAY"
      ? "Online Payment"
      : order.payment?.method?.toUpperCase() || "—",
},
                  { label: "Status",  value: order.payment?.status || "—",
                    badge: { bg: order.payment?.status === "paid" ? "#e1f5ee" : "#faeeda",
                             color: order.payment?.status === "paid" ? "#0f6e56" : "#854f0b" } },
                  { label: "Order ID", value: order.payment?.razorpayOrderId || "—", full: true },
                ].map((item) => (
                  <div key={item.label} style={{ gridColumn: item.full ? "1 / -1" : undefined }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#9ca3af", textTransform: "uppercase", marginBottom: 3 }}>
                      {item.label}
                    </div>
                    {item.badge ? (
                      <span style={{
                        background: item.badge.bg, color: item.badge.color,
                        fontSize: 12, fontWeight: 700, padding: "3px 10px", borderRadius: 20,
                      }}>
                        {item.value}
                      </span>
                    ) : (
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", wordBreak: "break-all" }}>
                        {item.value}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Estimated Delivery Card ── */}
            {order.estimatedDeliveryTime && (
              <div className="card padding" style={{ marginBottom: 0 }}>
                <CardHeader
                  iconBg="#faf5ff" iconStroke="#7c3aed"
                  title="Estimated Delivery"
                  icon={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>}
                />
                <div className="card-body mt-3" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#9ca3af", textTransform: "uppercase", marginBottom: 3 }}>
                      Date
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {formatDate(order.estimatedDeliveryTime)}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#9ca3af", textTransform: "uppercase", marginBottom: 3 }}>
                      Time
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {new Date(order.estimatedDeliveryTime).toLocaleTimeString("en-IN", {
                        hour: "2-digit", minute: "2-digit", hour12: true,
                      })}
                    </div>
                  </div>
                  {order.estimatedDuration && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "#9ca3af", textTransform: "uppercase", marginBottom: 3 }}>
                        Duration
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                        {order.estimatedDuration} min
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Errors */}
            {(error || trackingError) && (
              <div style={{
                background: "#fef2f2", color: "#dc2626",
                padding: "8px 12px", borderRadius: 8, fontSize: 12,
              }}>{trackingError || error}</div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderDetails;